import { useQuery } from '@tanstack/react-query'
import { financialAPI } from '@/lib/api-client'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  ArrowUpRight,
  ArrowDownRight,
  Wallet,
  Loader2,
  Receipt,
} from 'lucide-react'

function formatCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value)
}

function formatDate(date: string) {
  return new Date(date).toLocaleDateString('pt-BR')
}

interface EntityFinancialSummaryProps {
  entityType: string
  entityId: string
}

export function EntityFinancialSummary({ entityType, entityId }: EntityFinancialSummaryProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['entity-financial-summary', entityType, entityId],
    queryFn: () => financialAPI.getEntityFinancialSummary(entityType, entityId),
    enabled: !!entityId,
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    )
  }

  if (!data || data.txCount === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border bg-neutral-50 p-12">
        <Receipt className="h-12 w-12 text-neutral-300" />
        <h3 className="mt-4 text-lg font-medium text-neutral-900">
          Nenhuma transação financeira
        </h3>
        <p className="mt-2 text-sm text-neutral-500">
          Não há transações vinculadas a esta entidade na conciliação.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-green-100 p-2">
                <ArrowUpRight className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-neutral-500">Entradas</p>
                <p className="text-lg font-semibold text-green-600">
                  {formatCurrency(data.totalIncome)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-red-100 p-2">
                <ArrowDownRight className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-xs text-neutral-500">Saídas</p>
                <p className="text-lg font-semibold text-red-600">
                  {formatCurrency(data.totalExpense)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-blue-100 p-2">
                <Wallet className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-neutral-500">Saldo</p>
                <p className={`text-lg font-semibold ${data.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(data.balance)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Transactions table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Transações Recentes ({data.txCount})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Descrição</TableHead>
                <TableHead>Projeto</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead className="text-right">Valor</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.transactions.map((tx: any) => (
                <TableRow key={tx.id}>
                  <TableCell className="text-sm">
                    {formatDate(tx.date)}
                  </TableCell>
                  <TableCell className="text-sm max-w-[300px] truncate">
                    {tx.description}
                  </TableCell>
                  <TableCell className="text-sm text-neutral-500">
                    {tx.project?.name || '—'}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={
                        tx.type === 'INCOME'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }
                    >
                      {tx.type === 'INCOME' ? 'Entrada' : 'Saída'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right text-sm font-medium">
                    <span
                      className={
                        tx.type === 'INCOME'
                          ? 'text-green-600'
                          : 'text-red-600'
                      }
                    >
                      {tx.type === 'INCOME' ? '+' : '-'}
                      {formatCurrency(tx.amount)}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}
